/**
 * The Led AI design system's icon set (`_ds/led-ai-design-system-f7c932…/
 * components/core/Icon.jsx`), glyph for glyph: 24×24 thin-stroke line icons
 * drawn in `color` (default `currentColor`). The glasses and device glyphs
 * keep their own wider aspect, as the source does.
 *
 * Only the glyphs the console draws are here; adding one means copying its
 * path from the design system, not drawing a new one.
 */

import type { CSSProperties, ReactElement } from "react";

export type DsIconName =
  | "home" | "glasses" | "search" | "person" | "plus" | "chevron" | "dots" | "close"
  | "check" | "bolt" | "pin" | "camera" | "list" | "calendar" | "filter" | "flag"
  | "send" | "download" | "device" | "arrowRight";

type Draw = (c: string, s: number) => ReactElement;

const PATHS: Record<DsIconName, Draw> = {
  home: (c, s) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none">
      <path d="M3 10.5L12 3l9 7.5V20a1 1 0 01-1 1h-5v-6h-6v6H4a1 1 0 01-1-1v-9.5z" stroke={c} strokeWidth="1.7" strokeLinejoin="round" />
    </svg>
  ),
  glasses: (c, s) => (
    <svg width={s} height={s * 0.6} viewBox="0 0 28 16" fill="none">
      <circle cx="6" cy="9" r="5" stroke={c} strokeWidth="1.7" />
      <circle cx="22" cy="9" r="5" stroke={c} strokeWidth="1.7" />
      <path d="M11 9h6M2 4l-1-2M26 4l1-2" stroke={c} strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  ),
  search: (c, s) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none">
      <circle cx="11" cy="11" r="7" stroke={c} strokeWidth="1.7" />
      <path d="M20 20l-4-4" stroke={c} strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  ),
  person: (c, s) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="8" r="4" stroke={c} strokeWidth="1.7" />
      <path d="M4 21c0-4.4 3.6-8 8-8s8 3.6 8 8" stroke={c} strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  ),
  plus: (c, s) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none">
      <path d="M12 5v14M5 12h14" stroke={c} strokeWidth="2.2" strokeLinecap="round" />
    </svg>
  ),
  arrowRight: (c, s) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none">
      <path d="M5 12h14M13 6l6 6-6 6" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  chevron: (c, s) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none">
      <path d="M9 5l7 7-7 7" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  dots: (c, s) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none">
      <circle cx="5" cy="12" r="2" fill={c} />
      <circle cx="12" cy="12" r="2" fill={c} />
      <circle cx="19" cy="12" r="2" fill={c} />
    </svg>
  ),
  close: (c, s) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none">
      <path d="M5 5l14 14M19 5L5 19" stroke={c} strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  ),
  check: (c, s) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none">
      <path d="M4 12.5l5 5L20 6" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  bolt: (c, s) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none">
      <path d="M13 2L4 14h6l-2 8 9-12h-6l2-8z" fill={c} />
    </svg>
  ),
  pin: (c, s) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none">
      <path d="M12 2C8 2 5 5 5 8.8 5 14 12 22 12 22s7-8 7-13.2C19 5 16 2 12 2z" stroke={c} strokeWidth="1.6" />
      <circle cx="12" cy="9" r="2.6" fill={c} />
    </svg>
  ),
  camera: (c, s) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none">
      <path d="M3 7a2 2 0 012-2h2.5L9 3h6l1.5 2H19a2 2 0 012 2v11a2 2 0 01-2 2H5a2 2 0 01-2-2V7z" stroke={c} strokeWidth="1.5" />
      <circle cx="12" cy="12.5" r="3.5" stroke={c} strokeWidth="1.5" />
    </svg>
  ),
  list: (c, s) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none">
      <path d="M4 6h16M4 12h16M4 18h16" stroke={c} strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  ),
  calendar: (c, s) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none">
      <rect x="3" y="5" width="18" height="16" rx="2" stroke={c} strokeWidth="1.5" />
      <path d="M7 2v4M17 2v4M3 10h18" stroke={c} strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  ),
  filter: (c, s) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none">
      <path d="M3 5h18l-7 8.5V20l-4 2v-8.5L3 5z" stroke={c} strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
  ),
  flag: (c, s) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none">
      <path d="M5 22V3m0 0h12l-2 4 2 4H5" stroke={c} strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  ),
  send: (c, s) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none">
      <path d="M21 3L10 14M21 3l-7 18-4-7-7-4 18-7z" stroke={c} strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  ),
  download: (c, s) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none">
      <path d="M12 3v12m0 0l-4-4m4 4l4-4M4 19h16" stroke={c} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  device: (c, s) => (
    <svg width={s} height={s * 0.55} viewBox="0 0 28 16" fill="none">
      <circle cx="6" cy="8" r="5" stroke={c} strokeWidth="1.7" />
      <circle cx="22" cy="8" r="5" stroke={c} strokeWidth="1.7" />
      <path d="M11 8h6" stroke={c} strokeWidth="1.7" />
    </svg>
  ),
};

export function DsIcon({
  name,
  size = 22,
  color = "currentColor",
  style,
}: {
  name: DsIconName;
  size?: number;
  color?: string;
  style?: CSSProperties;
}) {
  return (
    <span aria-hidden style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", flex: "none", color, ...style }}>
      {PATHS[name](color, size)}
    </span>
  );
}
